import { TripsLayer } from '@deck.gl/geo-layers'
import { LineLayer, PathLayer, ScatterplotLayer } from '@deck.gl/layers'
import { MapboxOverlay } from '@deck.gl/mapbox'
import type { Trip } from '@transp/shared'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Theme } from '../theme'
import { basemapStyle, labelAnchorId, tuneBasemap } from './basemap'
import { type Bus, convoysAt } from './buses'

/** Centro do corredor 1758/1759. Zoom que enquadra o trecho compartilhado. */
const INITIAL = { center: [-46.64, -23.5] as [number, number], zoom: 12.2 }

export type RGB = [number, number, number]

/**
 * A paleta do dado desenhado, um conjunto POR TEMA.
 *
 * O deck.gl desenha em canal RGB, nao em CSS, entao ele nao enxerga as variaveis do
 * tema. As cores precisam existir aqui em numero. E como a legenda tem que combinar
 * com o mapa exatamente, ela le DESTE objeto (ver `cssColor`) em vez de repetir os
 * valores em CSS - duas listas de cor divergem no primeiro ajuste.
 *
 * POR QUE NAO SAO OS MESMOS VALORES NOS DOIS TEMAS: o ambar que brilha sobre preto
 * some sobre branco. Cada tema tem o seu par de matizes, escolhido para ter contraste
 * contra o SEU fundo, mantendo a distincao por matiz (nao por luminosidade) para quem
 * enxerga cor mal.
 */
export type Palette = {
	lines: Record<string, RGB>
	fallback: RGB
	selection: RGB
	/** Contorno do disco em movimento. Separa dois onibus que se cobrem. */
	outline: RGB
	convoy: [number, number, number, number]
	convoyMixed: [number, number, number, number]
	routeAlpha: number
	routeAlphaDim: number
	trailAlpha: number
	trailAlphaDim: number
	/** Opacidade do disco quando a posicao foi MANTIDA (sem medicao no instante). */
	heldAlpha: number
	/** Opacidade do preenchimento quando o onibus esta parado: o anel e que carrega. */
	stoppedAlpha: number
}

export const PALETTES: Record<Theme, Palette> = {
	escuro: {
		lines: { '1758': [235, 178, 60], '1759': [196, 120, 220] },
		fallback: [120, 200, 220],
		// Selecao, contorno e comboio saem da TINTA e do PAPEL do tema, nunca de um
		// terceiro matiz: as duas linhas sao as unicas coisas saturadas na tela.
		selection: [242, 243, 245],
		outline: [20, 22, 26],
		convoy: [155, 161, 170, 150],
		convoyMixed: [242, 243, 245, 205],
		routeAlpha: 38,
		routeAlphaDim: 22,
		trailAlpha: 235,
		trailAlphaDim: 55,
		heldAlpha: 90,
		stoppedAlpha: 45,
	},
	claro: {
		lines: { '1758': [166, 104, 12], '1759': [112, 48, 168] },
		fallback: [20, 110, 130],
		selection: [30, 32, 36],
		outline: [255, 255, 255],
		convoy: [107, 111, 118, 175],
		convoyMixed: [30, 32, 36, 220],
		// Alfa mais alto no claro: linha translucida sobre fundo claro perde contraste
		// mais depressa do que sobre fundo escuro, entao o mesmo valor sumiria.
		routeAlpha: 70,
		routeAlphaDim: 38,
		trailAlpha: 235,
		trailAlphaDim: 60,
		heldAlpha: 80,
		stoppedAlpha: 40,
	},
}

/** Cor de linha como string CSS, para a legenda combinar com o mapa. */
export function cssColor(c: RGB, alpha = 1): string {
	return alpha === 1 ? `rgb(${c[0]} ${c[1]} ${c[2]})` : `rgb(${c[0]} ${c[1]} ${c[2]} / ${alpha})`
}

export function lineColor(p: Palette, line: string): RGB {
	return p.lines[line] ?? p.fallback
}

/**
 * O TAMANHO DO ONIBUS ACOMPANHA O ZOOM, com piso e teto.
 *
 * Antes era fixo em 5 px em qualquer zoom, e isso tem um efeito perverso: quanto mais se
 * aproxima, MENOR o onibus fica em relacao ao que esta em volta - as ruas se abrem, o
 * rastro se espalha, e o disco continua do mesmo tamanhinho. Quem aproxima esta querendo
 * examinar um veiculo, e e justamente ai que ele encolhe de importancia.
 *
 * O raio passa a ser dado em METROS, entao cresce sozinho com a aproximacao, entre um piso
 * e um teto em pixels. Os 75 m nao sao o tamanho de um onibus - um onibus tem uns 12 m, que
 * no enquadramento de trabalho dariam menos de 1 px. E um SIMBOLO dimensionado para a
 * escala em que se olha, nao uma planta baixa.
 *
 * O PISO E QUASE O TAMANHO ANTIGO, de proposito. No enquadramento inicial os discos
 * dividem a tela com o traçado de todas as viagens da janela, e disco grande ali some
 * dentro do proprio rastro em vez de se destacar dele. O ganho de tamanho fica reservado
 * para quando alguem aproxima, que e quando ha espaco para ele.
 *
 * Medido na latitude do corredor: no enquadramento inicial (z 12,9) 1 px vale 18,4 m e o
 * disco fica no piso de 5,5 px; em z14, 8,6 px; de z14,4 em diante encosta no teto de
 * 11 px. Afastando, o piso impede que a frota vire poeira.
 */
const BUS_RADIUS_M = 75
const BUS_RADIUS_MIN_PX = 5.5
const BUS_RADIUS_MAX_PX = 11

/**
 * O halo da selecao cresce junto, e um pouco mais depressa.
 *
 * Um fator sobre o mesmo raio em metros, e nao "raio + 5 px": com o mesmo raio em metros
 * nos dois, fora das faixas de corte os dois circulos teriam exatamente o mesmo tamanho e
 * o anel sumiria dentro do disco.
 */
const HALO_FACTOR = 1.7

/**
 * Quanto esperar antes de decidir que o basemap veio vazio.
 *
 * Generoso de proposito. A primeira pintura do basemap leva alguns segundos em rede
 * comum, e ja houve tempo perdido aqui investigando como defeito uma tela que so estava
 * carregando. Um aviso que aparece cedo demais treina quem olha a ignora-lo.
 */
const BLANK_CHECK_MS = 12_000

export function MapView({
	trips,
	buses,
	currentTime,
	trailLength,
	selected,
	onSelect,
	theme,
}: {
	trips: Trip[]
	buses: Bus[]
	currentTime: number
	trailLength: number
	selected: number | null
	onSelect: (tripId: number | null) => void
	theme: Theme
}) {
	// Falha de fonte do basemap nao pode ser silenciosa. Quando a fonte vetorial nao
	// carrega, o MapLibre desenha o que consegue e o resultado e uma tela preta que passa
	// por mapa escuro. Foi exatamente isso que aconteceu com o PMTiles ausente no R2.
	const [styleError, setStyleError] = useState<string | null>(null)
	// Basemap que carrega "com sucesso" e nao desenha nada. Ver `checkBasemapPainted`.
	const [basemapBlank, setBasemapBlank] = useState(false)
	const container = useRef<HTMLDivElement>(null)
	const map = useRef<maplibregl.Map | null>(null)
	const overlay = useRef<MapboxOverlay | null>(null)
	// Id da primeira camada de rotulo do estilo carregado. Descoberto em runtime, nunca
	// fixado: o nome muda entre basemaps, e apontar para uma camada inexistente faz o
	// deck.gl lancar a cada frame e nao desenhar nada.
	const [labelLayerId, setLabelLayerId] = useState<string | undefined>()
	// Camera preservada entre trocas de tema. Trocar o tema recria o mapa (ver abaixo);
	// sem isto, quem estivesse acompanhando um trecho especifico voltaria ao enquadramento
	// inicial so por ter clicado no botao de tema.
	const camera = useRef<{ center: [number, number]; zoom: number; bearing: number } | null>(null)

	// O desenho depende do ZOOM (a separacao e medida em pixels), entao ele precisa ser
	// refeito a cada movimento da camera, nao so a cada instante do relogio. Passar isso
	// por estado do React recriaria o componente a 60 Hz durante um arrasto; a funcao de
	// desenho fica numa ref e o mapa a chama direto.
	const draw = useRef<() => void>(() => {})
	const onSelectRef = useRef(onSelect)
	onSelectRef.current = onSelect

	// O mapa e criado uma vez POR TEMA. Recria-lo a cada render perderia a posicao da
	// camera e recarregaria todos os tiles.
	//
	// POR QUE RECRIAR NA TROCA DE TEMA em vez de `setStyle`: em modo interleaved o deck.gl
	// insere as camadas dele na pilha do MapLibre, e `setStyle` para um estilo diferente
	// limpa essa pilha sem avisar o overlay - o mapa volta com o basemap novo e sem dado
	// nenhum por cima. Recriar e mais caro (os tiles seriam recarregados de qualquer jeito)
	// e nao tem estado escondido: a camera e restaurada explicitamente logo abaixo.
	useEffect(() => {
		if (!container.current) return
		const start = camera.current
		// Zerado antes de qualquer desenho: o id da camada de rotulo pertence ao estilo
		// ANTERIOR e nao existe no novo.
		setLabelLayerId(undefined)
		setStyleError(null)
		setBasemapBlank(false)

		const m = new maplibregl.Map({
			container: container.current,
			style: basemapStyle(theme),
			center: start?.center ?? INITIAL.center,
			zoom: start?.zoom ?? INITIAL.zoom,
			bearing: start?.bearing ?? 0,
			attributionControl: { compact: true },
		})
		m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
		m.on('load', () => {
			tuneBasemap(m)
			setLabelLayerId(labelAnchorId(m))
			draw.current()
		})
		m.on('move', () => draw.current())
		// Conferencia por RELOGIO, e nao no evento `idle`: com o overlay do deck.gl
		// redesenhando a cada quadro, o mapa pode nunca chegar a ficar ocioso, e um
		// detector pendurado em `idle` simplesmente nunca roda - o que e pior que nao ter
		// detector, porque parece que tem.
		//
		// SO VALE COM A ABA VISIVEL. Aba em segundo plano tem o `requestAnimationFrame`
		// congelado pelo navegador; sem quadro, o MapLibre nao desenha NEM pede tile, e
		// `queryRenderedFeatures` devolve zero num mapa perfeitamente sadio. Medido em
		// producao com a aba oculta: `visibilityState` "hidden", nenhuma requisicao .mvt,
		// zero feicoes - e a mesma tela preta que, com a aba a frente, aparece completa.
		// Sem esta guarda o aviso apareceria para quem trocou de aba e voltou.
		let blankTimer: ReturnType<typeof setTimeout>
		const armBlankCheck = () => {
			clearTimeout(blankTimer)
			document.removeEventListener('visibilitychange', onVisibility)
			blankTimer = setTimeout(() => {
				if (document.visibilityState !== 'visible') return
				setBasemapBlank(!basemapHasData(m))
			}, BLANK_CHECK_MS)
		}
		const onVisibility = () => {
			if (document.visibilityState === 'visible') armBlankCheck()
		}
		armBlankCheck()
		document.addEventListener('visibilitychange', onVisibility)
		m.on('error', (e) => {
			const msg = e.error?.message ?? String(e.error ?? 'erro desconhecido')
			setStyleError(msg)
			console.error('[basemap]', msg)
		})
		// Afordancia de diagnostico: com ?debug na URL, o mapa fica acessivel no console.
		if (new URLSearchParams(window.location.search).has('debug')) {
			;(window as unknown as { __map?: maplibregl.Map }).__map = m
		}

		const o = new MapboxOverlay({
			// interleaved: o deck.gl entra no MESMO contexto WebGL do mapa, o que permite
			// desenhar as trajetorias por baixo dos rotulos.
			interleaved: true,
			layers: [],
		})
		m.addControl(o)

		// Selecao pelo clique. O raio de captura e maior que o disco de proposito: acertar
		// um alvo de 5 px em movimento e frustrante, e errar por um pixel deseleciona.
		m.on('click', (e) => {
			const hit = o.pickObject({ x: e.point.x, y: e.point.y, radius: 12, layerIds: ['onibus'] })
			const bus = hit?.object as Bus | undefined
			onSelectRef.current(bus ? bus.tripId : null)
		})

		map.current = m
		overlay.current = o
		return () => {
			clearTimeout(blankTimer)
			const c = m.getCenter()
			camera.current = { center: [c.lng, c.lat], zoom: m.getZoom(), bearing: m.getBearing() }
			o.finalize()
			m.remove()
			map.current = null
			overlay.current = null
		}
	}, [theme])

	// Enquadra o mapa na extensao real do dado, uma vez por conjunto de trajetorias.
	const framed = useRef<string>('')
	useEffect(() => {
		const m = map.current
		if (!m || trips.length === 0) return
		const key = trips.map((t) => t.tripId).join(',')
		if (framed.current === key) return
		let west = 180
		let south = 90
		let east = -180
		let north = -90
		for (const trip of trips) {
			for (const [lon, lat] of trip.path) {
				if (lon < west) west = lon
				if (lon > east) east = lon
				if (lat < south) south = lat
				if (lat > north) north = lat
			}
		}
		if (west > east) return
		framed.current = key
		m.fitBounds(
			[
				[west, south],
				[east, north],
			],
			{ padding: { top: 40, bottom: 110, left: 280, right: 40 }, duration: 0 },
		)
	}, [trips])

	const render = useCallback(() => {
		const m = map.current
		const o = overlay.current
		if (!m || !o) return
		const p = PALETTES[theme]
		const before = <P extends object>(props: P): P => withBeforeId(props, m, labelLayerId)

		// CADA ONIBUS NA SUA POSICAO MEDIDA. Nada e movido, nunca.
		//
		// Houve uma tentativa de afastar os discos que se cobriam. Medida depois de pronta:
		// no zoom inicial 1 px vale 15,25 m, entao o limiar de 12 px exigia 183 m de
		// separacao real e empurrava 69,7% da frota o tempo todo. O remedio ficou mais
		// visivel que a doenca. A oclusao continua sendo um problema de verdade, e sera
		// resolvida por um caminho que nao mexa em coordenada.
		const placed = buses.map((b) => ({ bus: b, at: b.position }))
		const chosen =
			selected === null ? null : (placed.find((x) => x.bus.tripId === selected) ?? null)

		// Quando ha um carro escolhido, todo o resto recua para o fundo. Seguir um veiculo
		// especifico foi o pedido; sem essa quebra de contraste a tela nao ajuda a seguir.
		const dim = selected !== null

		// Comboios: pares do MESMO sentido a menos de 250 m. Vira LIGACAO entre os pontos,
		// nunca fusao - fundir escondia carro e apagava justamente o que se quer mostrar.
		const convoyLinks = convoysAt(buses).flatMap((c) =>
			c.links.map(([from, to]) => ({ from, to, mixed: c.mixed })),
		)
		const trailAlpha = (t: Trip) =>
			dim ? (t.tripId === selected ? 255 : p.trailAlphaDim) : p.trailAlpha
		const routeAlpha = dim ? p.routeAlphaDim : p.routeAlpha

		o.setProps({
			layers: [
				// 1. A ROTA, estatica e apagada. Sem ela o rastro animado nao tem sujeito.
				new PathLayer<Trip>(
					before({
						id: 'rota',
						data: trips,
						getPath: (d) => d.path,
						getColor: (d) => [...lineColor(p, d.line), routeAlpha],
						widthMinPixels: 1.5,
						widthMaxPixels: 3,
						parameters: { depthCompare: 'always' },
						updateTriggers: { getColor: [routeAlpha, theme] },
					}),
				),

				// 2. O rastro animado.
				new TripsLayer<Trip>(
					before({
						id: 'trips',
						data: trips,
						getPath: (d) => d.path,
						// times em segundos desde o inicio da janela: o TripsLayer guarda
						// timestamp em float32 e epoch em ms perderia precisao.
						getTimestamps: (d) => d.times,
						getColor: (d) => [...lineColor(p, d.line), trailAlpha(d)],
						currentTime,
						trailLength,
						capRounded: true,
						jointRounded: true,
						widthMinPixels: 3,
						updateTriggers: { getColor: [selected, theme] },
					}),
				),

				// 3. O comboio: linha ligando onibus do mesmo sentido que se alcancaram.
				//    Destacado quando o comboio tem as DUAS linhas - e o achado do projeto
				//    acontecendo na tela, e nao um detalhe de renderizacao.
				new LineLayer<(typeof convoyLinks)[number]>(
					before({
						id: 'comboio',
						data: convoyLinks,
						getSourcePosition: (d) => d.from,
						getTargetPosition: (d) => d.to,
						getColor: (d) => (d.mixed ? p.convoyMixed : p.convoy),
						widthUnits: 'pixels',
						getWidth: (d) => (d.mixed ? 2.4 : 1.6),
						updateTriggers: {
							getSourcePosition: currentTime,
							getTargetPosition: currentTime,
							getColor: [currentTime, theme],
							getWidth: currentTime,
						},
					}),
				),

				// 4. O halo do carro escolhido, para nao perde-lo de vista.
				new ScatterplotLayer<NonNullable<typeof chosen>>(
					before({
						id: 'halo',
						data: chosen ? [chosen] : [],
						getPosition: (d) => d.at,
						stroked: true,
						filled: false,
						getLineColor: p.selection,
						lineWidthUnits: 'pixels',
						getLineWidth: 1.6,
						radiusUnits: 'meters',
						getRadius: BUS_RADIUS_M * HALO_FACTOR,
						radiusMinPixels: BUS_RADIUS_MIN_PX * HALO_FACTOR,
						radiusMaxPixels: BUS_RADIUS_MAX_PX * HALO_FACTOR,
						updateTriggers: { getPosition: currentTime, getLineColor: theme },
					}),
				),

				// 5. O onibus. Tres estados, todos legiveis a distancia:
				//    cheio = andando; anel = parado; translucido = posicao MANTIDA (sem dado).
				new ScatterplotLayer<(typeof placed)[number]>(
					before({
						id: 'onibus',
						data: placed,
						getPosition: (d) => d.at,
						getFillColor: (d) => {
							const c = lineColor(p, d.bus.line)
							if (d.bus.held) return [...c, p.heldAlpha]
							if (d.bus.stopped) return [...c, p.stoppedAlpha]
							return [...c, 255]
						},
						getLineColor: (d) => (d.bus.stopped ? lineColor(p, d.bus.line) : p.outline),
						stroked: true,
						lineWidthUnits: 'pixels',
						getLineWidth: (d) => (d.bus.stopped ? 1.8 : 1.4),
						radiusUnits: 'meters',
						getRadius: BUS_RADIUS_M,
						radiusMinPixels: BUS_RADIUS_MIN_PX,
						radiusMaxPixels: BUS_RADIUS_MAX_PX,
						pickable: true,
						updateTriggers: {
							getPosition: currentTime,
							getFillColor: [currentTime, theme],
							getLineColor: [currentTime, theme],
							getLineWidth: currentTime,
						},
					}),
				),
			],
		})
	}, [trips, buses, currentTime, trailLength, labelLayerId, selected, theme])

	useEffect(() => {
		draw.current = render
		render()
	}, [render])

	// h-full em vez de absolute inset-0: o CSS do MapLibre aplica `position: relative`
	// na propria div do container, o que anula o posicionamento absoluto e faz a altura
	// colapsar para zero.
	return (
		<div className="relative h-full w-full">
			<div ref={container} className="h-full w-full" />
			{styleError && (
				<div className="absolute inset-x-0 top-0 z-20 bg-red-600 px-4 py-2 text-center text-xs text-white">
					Mapa de fundo falhou: {styleError}
				</div>
			)}
			{!styleError && basemapBlank && (
				<div className="absolute inset-x-0 top-0 z-20 bg-warn px-4 py-2 text-center text-xs text-black">
					O mapa de fundo veio vazio — as ruas e os nomes de bairro não têm dado para desenhar. As
					trajetórias são o dado medido e continuam corretas.
				</div>
			)}
		</div>
	)
}

/**
 * O basemap tem DADO para desenhar?
 *
 * POR QUE EXISTE: o modo de falha que ja custou sessoes a este projeto foi o PMTiles
 * ausente no R2. A fonte vetorial falhava, o MapLibre desenhava as camadas que conseguia
 * (nenhuma, porque todas dependiam dela) e sobrava a cor de fundo - uma tela preta que
 * passa por mapa escuro. `map.on('error')` cobre parte disso, mas fonte que responde e
 * devolve pouco nao gera erro nenhum.
 *
 * O QUE ELE NAO DETECTA, e e importante nao confundir: mapa ainda CARREGANDO. Durante a
 * primeira pintura as feicoes ja estao na arvore de renderizacao e esta funcao devolve
 * verdadeiro, enquanto a tela ainda esta preta por alguns segundos. Isso e deliberado -
 * o aviso e sobre o basemap vir vazio, nao sobre ele vir devagar - e e a razao de a
 * conferencia so acontecer depois de BLANK_CHECK_MS. Quem investigar tela preta antes
 * desse prazo esta investigando o carregamento, nao um defeito. Ja aconteceu duas vezes.
 *
 * As camadas do deck.gl ficam de fora porque nao tem `source` - se entrassem, o nosso
 * proprio dado responderia pelo basemap e o aviso nunca apareceria.
 */
function basemapHasData(map: maplibregl.Map): boolean {
	const layers = (map.getStyle().layers ?? [])
		// `'source' in l` ja exclui a camada de fundo: no tipo do MapLibre so ela nao tem
		// fonte, e ela pinta cor solida - contaria como "desenhou" mesmo com tudo vazio.
		.filter((l) => 'source' in l && l.source)
		.map((l) => l.id)
		.filter((id) => map.getLayer(id))
	if (layers.length === 0) return false
	return map.queryRenderedFeatures({ layers }).length > 0
}

/**
 * `beforeId` so existe em modo interleaved: e o MapboxOverlay que o consome para
 * inserir a camada do deck.gl numa posicao especifica da pilha do MapLibre. Nao
 * faz parte do tipo da camada, entao entra por aqui, num lugar so e com a razao
 * escrita, em vez de espalhar cast pelo componente.
 *
 * A CONSULTA A `map.getLayer` NAO E ZELO EXCESSIVO: o id vem de estado do React e o
 * estilo do mapa troca junto com o tema. Existe uma janela em que o id ainda aponta
 * para uma camada do estilo anterior, e pedir `beforeId` de camada inexistente faz o
 * deck.gl lancar a cada quadro e nao desenhar nada. Perguntar ao mapa elimina a
 * classe inteira, em vez de depender da ordem em que o React resolve os efeitos.
 */
function withBeforeId<P extends object>(
	props: P,
	map: maplibregl.Map,
	beforeId: string | undefined,
): P {
	return beforeId && map.getLayer(beforeId) ? ({ ...props, beforeId } as P) : props
}
